import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  NOTIFICATION_PREFERENCE_FIELDS,
  normalizeNotificationPreferences,
  type NotificationPreferences,
} from '../../notificationTaxonomy';

interface NotificationPrefsProps {
  officialId: string;
  initialPreferences?: NotificationPreferences | null;
  open: boolean;
  onToggle: () => void;
}

export default function NotificationPrefs({
  officialId,
  initialPreferences,
  open,
  onToggle,
}: NotificationPrefsProps) {
  const { token } = useAuth();
  const [prefs, setPrefs] = useState<NotificationPreferences>(
    normalizeNotificationPreferences(initialPreferences ?? null),
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!token) {
        setPrefs({ ...DEFAULT_NOTIFICATION_PREFERENCES });
        return;
      }

      if (initialPreferences) {
        setPrefs(normalizeNotificationPreferences(initialPreferences));
        return;
      }

      setLoading(true);
      try {
        const res = await fetch('/api/adoptions/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as {
          adoptions?: Array<{ official?: { id?: string }; notificationPreferences?: NotificationPreferences | null }>;
        };
        const match = (data.adoptions || []).find((adoption) => adoption.official?.id === officialId);
        if (!cancelled) {
          setPrefs(normalizeNotificationPreferences(match?.notificationPreferences ?? null));
        }
      } catch {
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [initialPreferences, officialId, token]);

  const toggle = (key: keyof NotificationPreferences) => {
    setSaved(false);
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    if (!token) return;

    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const res = await fetch(`/api/adoptions/${officialId}/notifications`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(normalizeNotificationPreferences(prefs)),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Failed to update preferences');
      }
      setSaved(true);
    } catch (err: any) {
      setError(err.message || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-navy-100 dark:border-navy-700">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-1 py-2 text-left"
        aria-expanded={open}
      >
        <span className="text-xs font-black uppercase tracking-widest text-navy-500 dark:text-cream-300">
          Alert Preferences
        </span>
        <span className="text-xs font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">
          {open ? 'Close' : 'Edit'}
        </span>
      </button>

      {open && (
        <div className="pb-3">
          {loading ? (
            <p className="py-2 text-xs text-navy-400 dark:text-navy-300">Loading preferences...</p>
          ) : (
            <>
              <div className="space-y-2">
                {NOTIFICATION_PREFERENCE_FIELDS.map(({ key, label, hint }) => (
                  <label key={key} className="flex cursor-pointer items-start gap-3 py-1.5">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!prefs[key]}
                      onClick={() => toggle(key)}
                      className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${
                        prefs[key] ? 'bg-gold-500' : 'bg-navy-200 dark:bg-navy-700'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition ${
                          prefs[key] ? 'translate-x-4' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className="min-w-0">
                      <span className="block text-xs font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
                        {label}
                      </span>
                      <span className="block text-[10px] text-navy-400 dark:text-navy-300">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>

              {error && (
                <p className="mt-2 text-xs font-bold uppercase tracking-widest text-red-500">{error}</p>
              )}
              {saved && !error && (
                <p className="mt-2 text-xs font-bold uppercase tracking-widest text-green-600 dark:text-green-400">
                  Preferences saved
                </p>
              )}

              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="mt-3 w-full rounded-lg bg-navy-700 px-4 py-2 text-xs font-black uppercase tracking-widest text-cream-50 transition hover:bg-navy-800 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
